import React, { useLayoutEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// Layout Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CustomCursor from "./components/CustomCursor";
import Analytics from "./components/Analytics";

// Page Components
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import ServicePage from "./pages/ServicePage";
import Expertise from "./pages/Expertise";
import Team from "./pages/Team";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";

import servicePages from "./data/servicePages";
import {
  SITE_URL,
  SITE_NAME,
  OG_IMAGE,
  getMeta,
  notFoundMeta,
  normalizePath,
  schemasFor,
} from "./seo";

function PageMetadata() {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);
  const meta = getMeta(path);
  const info = meta || notFoundMeta;
  const url = `${SITE_URL}${path === "/" ? "/" : path}`;
  return (
    <Helmet>
      <title>{info.title}</title>
      <meta name="description" content={info.description} />
      {meta ? (
        <link rel="canonical" href={url} />
      ) : (
        <meta name="robots" content="noindex" />
      )}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={info.title} />
      <meta property="og:description" content={info.description} />
      {meta && <meta property="og:url" content={url} />}
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="The Alpha CPAs logo on a navy background" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={info.title} />
      <meta name="twitter:description" content={info.description} />
      <meta name="twitter:image" content={OG_IMAGE} />
      {meta &&
        schemasFor(path).map((schema, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
    </Helmet>
  );
}

function ScrollWrapper({ children }) {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);

  useLayoutEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      setDisplayLocation(location);
    }
  }, [location, displayLocation.pathname]);

  if (location.pathname !== displayLocation.pathname) {
    return null;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <PageMetadata />
      <Analytics />
      <div className="flex flex-col min-h-screen bg-white font-sans text-[#203468] overflow-x-hidden">
        <CustomCursor />
        <Navbar />

        <main className="flex-grow">
          <ScrollWrapper>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              {servicePages.map((p) => (
                <Route
                  key={p.slug}
                  path={`/services/${p.slug}`}
                  element={<ServicePage slug={p.slug} />}
                />
              ))}
              <Route path="/expertise" element={<Expertise />} />
              <Route path="/team" element={<Team />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </ScrollWrapper>
        </main>

        <Footer />
      </div>
    </>
  );
}
